import React, { createContext, useContext, ReactNode, useEffect, useState, useMemo, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import * as Sentry from '@sentry/react';
import { useCurrentUser } from '@/hooks/useAuth';
import { TokenService } from '@/services/tokenService';
import { setTokenGetter } from '@/services/api';
import { loginRequest } from '@/config/authConfig';

interface User {
  id?: string;
  email?: string;
  name?: string;
  role?: string;
  businessUnitId?: string;
  roles?: string[];
}

interface ErrorWithCode extends Error {
  code?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
  tokenService: TokenService;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [tokenService] = useState(() => new TokenService(instance));
  const [hasToken, setHasToken] = useState<boolean>(false);
  const [tokenError, setTokenError] = useState<boolean>(false);
  const { data: user, isLoading: isLoadingUser, error: userError } = useCurrentUser(hasToken && !tokenError);
  
  const isAuthenticated = accounts.length > 0 && hasToken && !!user && !tokenError;
  const isLoading = inProgress !== InteractionStatus.None || isLoadingUser;

 
  useEffect(() => {
    setTokenGetter(tokenService.getAccessToken.bind(tokenService));
  }, [tokenService]);

  
  useEffect(() => {
    const verifyToken = async () => {
      if (accounts.length > 0 && inProgress === InteractionStatus.None) {
        try {
          
          const token = await tokenService.getAccessToken();
          
          if (token) {
          
            setHasToken(true);
            setTokenError(false);
            
            // Set user context in Sentry for better error tracking
            Sentry.setUser({
              email: accounts[0]?.username,
              id: accounts[0]?.localAccountId,
            });
          } else {
            
            setHasToken(false);
            setTokenError(true);
            console.warn('⚠️ Token verification failed - no token returned');
          }
        } catch (error) {
          
          setHasToken(false);
          setTokenError(true);
          
          Sentry.captureException(error, {
            tags: { component: 'auth', action: 'verify_token' },
            extra: { accountCount: accounts.length },
          });
        }
      } else if (accounts.length === 0) {
        setHasToken(false);
        setTokenError(false);
        Sentry.setUser(null); // Clear user context when logged out
      }
    };

    verifyToken();
  }, [accounts, tokenService, inProgress]);

  // Monitor user fetch errors
  useEffect(() => {
    if (userError) {
      console.error('❌ Failed to fetch user from /api/auth/me:', userError);
      
      // Track authentication failures in Sentry with detailed context
      const errorWithCode = userError as ErrorWithCode;
      const errorDetails = {
        errorName: userError.name,
        errorMessage: userError.message,
        errorCode: errorWithCode.code,
        hasAccounts: accounts.length > 0,
        apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
      };
      
      console.error('❌ Authentication failed - Unable to fetch user profile', errorDetails);
      
      Sentry.captureException(userError, {
        tags: {
          component: 'auth',
          action: 'fetch_user',
          error_type: errorWithCode.code === 'ERR_NETWORK' ? 'network_error' : 'auth_error',
        },
        extra: errorDetails,
        fingerprint: ['auth-fetch-user-error', errorWithCode.code || 'unknown'],
      });
      
      setTokenError(true);
    }
  }, [userError, accounts.length]);

  const login = useCallback(() => {
    instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(() => {
    instance.logoutRedirect();
  }, [instance]);

  const contextValue = useMemo(
    () => ({
      user: user || null,
      isLoading,
      isAuthenticated,
      login,
      logout,
      tokenService,
    }),
    [user, isLoading, isAuthenticated, login, logout, tokenService]
  );
  
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
